/**
 * AgentIdentityLifecyclePanel — Identity redesign ID-3.
 *
 * The Agent Identity lifecycle view: the agent's canonical identity card
 * (state, WIMSE/spiffe id, credential profile, issuer, serial, verification
 * freshness, gateway-admits), the lifecycle timeline (from svid_lifecycle_events),
 * and the Assign/Issue/Rotate/Verify/Suspend/Resume/Revoke actions wired to the
 * ID-3 backend (AgentIdentityService state machine via the issuer SPI).
 *
 * Self-contained + additive: mounting it does not touch the existing seven-section
 * agent detail. Best-effort reads; actions return the updated view and re-render.
 * Revocation blast-radius is ID-4; this panel surfaces the lifecycle + actions.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Fingerprint, ShieldCheck, RefreshCw, Ban, Pause, Play, KeyRound, Clock, AlertTriangle, Radar,
} from 'lucide-react';
import { Button } from '../Button';
import { Pill } from '../Pill';
import { getAgentIdentityLifecycle, agentIdentityAction, getAgentBlastRadius } from '../../../services/api';

const STATE_TONE = {
  VERIFIED: 'success',
  ACTIVE: 'cyan',
  IDENTITY_ASSIGNED: 'warning',
  ROTATING: 'action',
  SUSPENDED: 'muted',
  REVOKED: 'danger',
  EXPIRED: 'muted',
  UNIDENTIFIED: 'muted',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
};

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-v2-ink-muted font-semibold">{label}</div>
      <div className="text-[12.5px] text-v2-ink mt-0.5 truncate">{children ?? '—'}</div>
    </div>
  );
}

function BlastRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-[12px]">
      <span className="text-v2-ink-secondary">{label}</span>
      <span className="font-medium text-v2-ink text-right">{value}</span>
    </div>
  );
}

export default function AgentIdentityLifecyclePanel({ agentId }) {
  const [lc, setLc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [blast, setBlast] = useState(null); // ID-4 pre-revoke blast-radius preview

  const load = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    try {
      const d = await getAgentIdentityLifecycle(agentId);
      // apiFetch resolves { ok, data, etag } — unwrap .data for the LifecycleView.
      if (d && d.ok === false) throw new Error(d.error || 'Failed to load identity lifecycle');
      setLc(d?.data ?? d);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Failed to load identity lifecycle');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { load(); }, [load]);

  const act = async (action, body) => {
    setBusy(action);
    try {
      const updated = await agentIdentityAction(agentId, action, body);
      if (updated && updated.ok === false) throw new Error(updated.error || `Action "${action}" failed`);
      setLc(updated?.data ?? updated);
      setError(null);
    } catch (e) {
      setError(e?.message || `Action "${action}" failed`);
    } finally {
      setBusy(null);
    }
  };

  const onRevoke = async () => {
    // ID-4 — compute + show the blast radius before confirming a hard revoke.
    setBusy('blast');
    try {
      const r = await getAgentBlastRadius(agentId);
      if (r && r.ok === false) throw new Error(r.error || 'Failed to compute blast radius');
      setBlast(r?.data ?? r);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Failed to compute blast radius');
    } finally {
      setBusy(null);
    }
  };

  const confirmRevoke = () => {
    setBlast(null);
    act('revoke', { reason: 'operator revoke from lifecycle view' });
  };

  if (loading && !lc) {
    return (
      <div className="rounded-v2-lg border border-v2-border-subtle p-4 mb-3 text-[12.5px] text-v2-ink-muted">
        Loading identity lifecycle…
      </div>
    );
  }
  if (error && !lc) {
    return (
      <div className="rounded-v2-lg border border-v2-border-subtle p-4 mb-3 flex items-center gap-2 text-[12.5px] text-v2-danger">
        <AlertTriangle size={15} aria-hidden="true" /> {error}
      </div>
    );
  }
  if (!lc) return null;

  const state = lc.state || 'UNIDENTIFIED';
  const verification = lc.verification || {};
  const isSuspended = state === 'SUSPENDED';

  return (
    <div className="rounded-v2-lg border border-v2-border-subtle overflow-hidden mb-3">
      {/* Navy header — agent + state */}
      <div className="bg-[#0a1d44] text-white px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
            <Fingerprint size={16} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="text-[14px] font-semibold leading-tight truncate">{lc.agentName || 'Agent'}</div>
            <div className="text-[11px] text-[#cadcfc]">Agent Identity Lifecycle</div>
          </div>
        </div>
        <Pill tone={STATE_TONE[state] || 'muted'} size="sm">{state.replace(/_/g, ' ').toLowerCase()}</Pill>
      </div>

      <div className="p-4">
        {/* WIMSE id */}
        <div className="flex items-center gap-2 bg-v2-muted rounded-v2-sm px-3 py-2 mb-1.5">
          <Fingerprint size={14} className="text-v2-ink-secondary flex-shrink-0" aria-hidden="true" />
          <code className="font-mono-v2 text-[11.5px] break-all">{lc.wimseId || '— (no identity assigned)'}</code>
        </div>
        <p className="text-[11px] text-v2-ink-muted mb-3">
          canonical WIMSE id · trust domain {lc.trustDomain || 'agentlets.local'}
        </p>

        {/* Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-2.5">
          <Field label="Credential profile">{lc.credentialProfile || '— (preview)'}</Field>
          <Field label="Issuer">{lc.issuerName}</Field>
          <Field label="Serial">{lc.serial || '—'}</Field>
          <Field label="Expires / rotates">{fmt(lc.expiresAt)}</Field>
          <Field label="Verification">
            {verification.valid
              ? <span className="text-v2-success">verified · {fmt(verification.checkedAt)}</span>
              : <span className="text-v2-ink-secondary">{verification.reason || 'not verified'}</span>}
          </Field>
          <Field label="Gateway admits">{lc.gatewayAdmits ? 'yes' : 'no'}</Field>
        </div>

        {/* Timeline */}
        <div className="border-t border-v2-border-subtle mt-3.5 pt-3">
          <div className="text-[11px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-2">Lifecycle timeline</div>
          {(!lc.timeline || lc.timeline.length === 0) ? (
            <p className="text-[12px] text-v2-ink-muted m-0">No lifecycle events yet.</p>
          ) : (
            <ul className="space-y-1.5 m-0 p-0 list-none">
              {lc.timeline.slice(0, 8).map((e, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Clock size={13} className="text-v2-ink-secondary mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div className="text-[12px] leading-snug">
                    <span className="font-medium">{e.eventType}</span>
                    <span className="text-v2-ink-secondary"> · {e.actor}</span>
                    <span className="text-v2-ink-muted"> · {fmt(e.occurredAt)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && (
          <div className="mt-3 flex items-center gap-2 text-[12px] text-v2-danger">
            <AlertTriangle size={14} aria-hidden="true" /> {error}
          </div>
        )}

        {/* ID-4 — revocation blast-radius preview (shown before a hard revoke) */}
        {blast && (
          <div className="mt-3.5 rounded-v2-md border border-v2-danger bg-v2-danger-soft p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <Radar size={15} className="text-v2-danger" aria-hidden="true" />
              <span className="text-[13px] font-semibold text-v2-danger">Revocation blast radius</span>
            </div>
            <p className="text-[12px] text-v2-ink m-0 mb-2.5">{blast.summary}</p>
            <div className="grid grid-cols-2 gap-x-5 gap-y-1.5 mb-3">
              <BlastRow label="Gateway / SDK / MCP keys" value={`${blast.gatewayKeys} → rejected`} />
              <BlastRow label="In-flight runs" value={`${blast.inFlightRuns} → sealed FAILED`} />
              <BlastRow label="Delegation children" value={`${blast.delegationChildren} → cascade-revoked`} />
              <BlastRow label="Federation peers" value={`${blast.federationPeers} → bundle pulled`} />
              <BlastRow label="Trust band" value={`${blast.trustBand || '—'} → surface dark`} />
            </div>
            <div className="flex gap-2">
              <Button variant="danger" size="sm" iconLeft={<Ban size={13} />} disabled={!!busy} onClick={confirmRevoke}>
                {busy === 'revoke' ? 'Revoking…' : 'Confirm revoke'}
              </Button>
              <Button variant="secondary" size="sm" disabled={!!busy} onClick={() => setBlast(null)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mt-3.5">
          {state === 'UNIDENTIFIED' && (
            <Button variant="secondary" size="sm" iconLeft={<KeyRound size={13} />}
                    disabled={!!busy} onClick={() => act('assign')}>
              {busy === 'assign' ? 'Assigning…' : 'Assign identity'}
            </Button>
          )}
          <Button variant="primary" size="sm" iconLeft={<KeyRound size={13} />}
                  disabled={!!busy} onClick={() => act('issue')}>
            {busy === 'issue' ? 'Issuing…' : 'Issue'}
          </Button>
          <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={13} />}
                  disabled={!!busy} onClick={() => act('rotate')}>
            {busy === 'rotate' ? 'Rotating…' : 'Rotate'}
          </Button>
          <Button variant="secondary" size="sm" iconLeft={<ShieldCheck size={13} />}
                  disabled={!!busy} onClick={() => act('verify')}>
            {busy === 'verify' ? 'Verifying…' : 'Re-verify'}
          </Button>
          {isSuspended ? (
            <Button variant="secondary" size="sm" iconLeft={<Play size={13} />}
                    disabled={!!busy} onClick={() => act('resume')}>
              {busy === 'resume' ? 'Resuming…' : 'Resume'}
            </Button>
          ) : (
            <Button variant="secondary" size="sm" iconLeft={<Pause size={13} />}
                    disabled={!!busy} onClick={() => act('suspend')}>
              {busy === 'suspend' ? 'Suspending…' : 'Suspend'}
            </Button>
          )}
          <Button variant="danger" size="sm" iconLeft={<Ban size={13} />}
                  disabled={!!busy} onClick={onRevoke}>
            {busy === 'revoke' ? 'Revoking…' : 'Revoke'}
          </Button>
        </div>
      </div>
    </div>
  );
}
